import React, { useCallback, useContext, useState, useEffect, useRef } from "react";
import { Input } from "components/Inputs";
import { Button } from "v2/components/shared";
import { Col, Row, LoadingState } from "components/Shared";
import { AsyncSelect as BuildingLookup } from "v2/components/Buildings";
import { AsyncSelect as PropertyLookup } from "v2/components/Properties";
import { AsyncSelect as PrecinctLookup } from "v2/components/Precincts";
import { FloorAsyncSelect as FloorLookup } from "v2/components/Tenancies/";
import { AsyncSelect as TenancyLookup } from "v2/components/Tenancies";
import { NewProjectContext } from "v2/context";
import { useGetBuilding } from "v2/hooks";
import { parse as parseUrl } from "query-string";
import Datasource, { DatasourceResponse } from "lib/datasource";

// @ts-ignore
import validate from "validate.js";
import classNames from "classnames";
import _ from "lodash";

const constraints = {
	name: {
		presence: {
			message: "^ is required",
		},
	},
};

const Form = ({ defaultTenancy, onUpdate, tenancy }: { defaultTenancy?: any; onUpdate?: any; tenancy?: any }) => {
	const { template } = useContext(NewProjectContext);
	const [property, setProperty] = useState<any>(tenancy?.property);
	const [precinctId, setPrecinctId] = useState<string>("");
	const [buildingId, setBuildingId] = useState<string>("");
	const [floorId, setFloorId] = useState<string>("");
	const [kind, setKind] = useState(tenancy?.tenancy_kind);
	const [tenancyExist, setTenancyExist] = useState(false);
	const [tenancyId, setTenancyId] = useState(defaultTenancy);
	const [name, setName] = useState(tenancy?.name || "");
	const [area, setArea] = useState(tenancy?.area || 0);
	const [category, setCategory] = useState("");
	const [errors, setErrors] = useState();
	const { property_id } = parseUrl(window.location.search) as { property_id: string };
	const [defaultPropertyAddress, setDefaultPropertyAddress] = useState<string>(tenancy?.property?.name || "");
	const building = useGetBuilding(property?.id, buildingId);

	const getTenancyKindOptions = useCallback(
		() => [
			!!tenancyExist && "existing_tenancy",
			"new_tenancy",
			!_.includes(_.get(template, "tags"), "flinders") ? "unallocated_space" : false,
		],
		[tenancyExist, template]
	);

	const [datasource] = useState(new Datasource({ mainModelName: "property" }));
	const isPrecinctChangeFromBuilding = useRef(false);
	const previousPrecinctId = useRef<string>("");

	// Handle building selection and auto-select precinct
	const handleBuildingChange = useCallback((buildingId: string, precinctIdFromBuilding?: string) => {
		setBuildingId(buildingId);
		if (precinctIdFromBuilding) {
			isPrecinctChangeFromBuilding.current = true;
			setPrecinctId(precinctIdFromBuilding);
		}
	}, []);

	var startWithPropertyId = property_id && property && defaultPropertyAddress;

	//when property is selected, set the default property address
	useEffect(() => {
		let sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
			setProperty(response.normalizedMainModelResponse);
			setDefaultPropertyAddress(response.normalizedMainModelResponse.name);
		});
		return () => sub.unsubscribe();
	}, [datasource]);

	//when property_id is selected, get the property
	useEffect(() => {
		if (!_.isEmpty(property_id)) {
			datasource.get("v2", `properties/${property_id}`, {});
		}
	}, [datasource, property_id]);

	// Auto-select precinct when building is selected
	useEffect(() => {
		if (building && buildingId) {
			const precinctIdFromBuilding = building.precinct_id || (building.precinct && building.precinct.id);
			if (precinctIdFromBuilding) {
				isPrecinctChangeFromBuilding.current = true;
				setPrecinctId(precinctIdFromBuilding);
			}
		}
		// Don't clear precinct when building is cleared - let user keep their manual selection
	}, [building, buildingId]);

	// Reset building when precinct changes manually (not from building selection)
	useEffect(() => {
		// Track if precinct actually changed
		const precinctChanged = previousPrecinctId.current !== precinctId;
		
		if (isPrecinctChangeFromBuilding.current) {
			// Reset the flag after the change is processed
			isPrecinctChangeFromBuilding.current = false;
			previousPrecinctId.current = precinctId;
			return;
		}
		
		// Only process if precinct actually changed
		if (precinctChanged) {
			// If precinct changed manually and building is selected, reset building
			if (precinctId && buildingId) {
				// Check if current building belongs to the new precinct
				if (building) {
					const buildingPrecinctId = building.precinct_id || (building.precinct && building.precinct.id);
					if (buildingPrecinctId !== precinctId) {
						setBuildingId("");
						setFloorId("");
					}
				} else {
					// If building data not loaded yet, reset anyway to be safe
					setBuildingId("");
					setFloorId("");
				}
			} else if (!precinctId && buildingId) {
				// If precinct is cleared, also clear building
				setBuildingId("");
				setFloorId("");
			}
		}
		
		// Update previous precinct ID
		previousPrecinctId.current = precinctId;
	}, [precinctId, buildingId, building]);
	
	const onSubmit = () => {
		if (kind === "existing_tenancy" && tenancyId) {
			const tenancy = {
				id: tenancyId,
				building_id: buildingId,
				floor_id: floorId,
				tenancy_exist: tenancyExist,
				tenancy_kind: kind,
				property,
				building,
			};
			onUpdate(tenancy);
		}

		if (kind !== "existing_tenancy") {
			const finalName = kind === "unallocated_space" ? "Unallocated Space" : name;
			const tenancy = {
				building_id: buildingId,
				floor_id: floorId,
				tenancy_exist: tenancyExist,
				tenancy_kind: kind,
				name: finalName,
				area,
				property,
				building,
				kind: category,
			};
			const errors = validate(tenancy, constraints);
			!!errors ? setErrors(errors) : onUpdate(tenancy);
		}
	};

	if (startWithPropertyId || !property_id) {
		//const newArea = {area};
		return (
			<Row>
				<Col>
					<PropertyLookup
						onChange={setProperty}
						name="property"
						defaultState={{
							name: defaultPropertyAddress,
							errors,
						}}
					/>
					<PrecinctLookup 
						propertyId={property?.id}
						onChange={setPrecinctId} 
						defaultState={{ precinct_id: precinctId, errors }}
					/>
					<BuildingLookup precinctId={precinctId} propertyId={property?.id} onChange={handleBuildingChange} defaultState={{ building_id: buildingId, errors }} />
					<FloorLookup
						buildingId={buildingId}
						onChange={(floor_id: string, tenancy_exist: boolean) => {
							setFloorId(floor_id);
							setTenancyExist(tenancy_exist);
						}}
						defaultState={{ floor_id: "", errors }}
					/>
					<Input
						onChange={setKind}
						name="tenancy_kind"
						defaultState={{ tenancy_kind: kind, errors }}
						required={constraints}
						options={getTenancyKindOptions()}
						showIf={!!property}
					/>

					{kind === "existing_tenancy" && <TenancyLookup floorId={floorId} onChange={setTenancyId} />}

					<Input
						onChange={setName}
						name="name"
						defaultState={{ name, errors }}
						required={constraints}
						label={_.includes(_.get(template, "tags"), "flinders") ? "room_number" : "tenancy_name"}
						showIf={kind === "new_tenancy"}
					/>

					<Input
						onChange={setArea}
						name="area"
						defaultState={{ area, errors }}
						required={constraints}
						suffix="m²"
						type="number"
						min="0"
						showIf={kind === "new_tenancy" || kind === "unallocated_space"}
					/>
					<Input
						onChange={setCategory}
						name="category"
						defaultState={{ category, errors }}
						required={constraints}
						options={["speciality", "food", "kiosk", "food-kiosk"]}
						showIf={
							(kind === "new_tenancy" || kind === "unallocated_space") && _.includes(_.get(template, "tags"), "retail")
						}
					/>
					<Button
						label="Next"
						className={classNames("float-right text-white mt-5", {
							"bg-blue-100 hover:shadow-hover shadow-button": true,
						})}
						onClick={onSubmit}
					/>
				</Col>
			</Row>
		);
	}

	return <LoadingState />;
};

export default Form;
